/*
 * Copyright (c) 2026 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { computed, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { signalStore, withState, withComputed, withMethods, patchState } from '@ngrx/signals';
import { cloneDeep } from 'lodash';
import { Subject, Observable } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { normalizeFilters, TableFilters } from './filter-normalization.utils';
import { TableStateStoreInitConfig } from './table-state-store.model';

interface TableStateStoreState {
  filters: TableFilters;
  sortField: string | null;
  sortOrder: number;
  first: number;
  rows: number;
  initialized: boolean;
  defaultFilters: TableFilters;
  _urlStateKey: string;
  _transformFiltersForBackend: ((filters: TableFilters) => TableFilters) | null;
}

const initialState: TableStateStoreState = {
  filters: {},
  sortField: null,
  sortOrder: 1,
  first: 0,
  rows: 10,
  initialized: false,
  defaultFilters: {},
  _urlStateKey: '',
  _transformFiltersForBackend: null,
};

export const TableStateStore = signalStore(
  withState(initialState),

  withComputed((store) => ({
    filtersForBackend: computed(() => {
      const transform = store._transformFiltersForBackend();
      return transform ? transform(store.filters()) : store.filters();
    }),

    hasActiveFilters: computed(() => {
      const filters = store.filters();
      const keys = Object.keys(filters);
      if (keys.length === 0) return false;
      return !Object.values(filters).every((filter) => {
        if (Array.isArray(filter)) {
          return filter.every((metadata) => !metadata.value);
        } else if (filter) {
          return !filter.value;
        }
        return true;
      });
    }),
  })),

  withMethods((store) => {
    const router = inject(Router);
    const route = inject(ActivatedRoute);

    const _loadSubject = new Subject<void>();

    // Pre-built observable — map callback runs lazily, so store signals resolve correctly
    const _loadRequests$ = _loadSubject.pipe(
      debounceTime(300),
      map(() => ({
        filters: store.filtersForBackend(),
        sortField: store.sortField(),
        sortOrder: store.sortOrder(),
        first: store.first(),
        rows: store.rows(),
      })),
    );

    function extractUrlState(urlStateKey: string): {
      filters: TableFilters;
      sortField: string | null;
      sortOrder: number;
      first: number;
      rows: number;
    } {
      const stateParam = route.snapshot.queryParams[urlStateKey];
      const result = {
        filters: {} as TableFilters,
        sortField: null as string | null,
        sortOrder: 1,
        first: 0,
        rows: 10,
      };

      if (!stateParam) return result;

      try {
        const state = JSON.parse(decodeURIComponent(stateParam));
        if (state.filters) result.filters = normalizeFilters(state.filters);
        if (state.sortField) result.sortField = state.sortField;
        if (state.sortOrder != null) result.sortOrder = state.sortOrder;
        if (state.first != null) result.first = state.first;
        if (state.rows != null) result.rows = state.rows;
      } catch {
        // ignore invalid URL state
      }

      return result;
    }

    function syncToUrl(): void {
      const urlStateKey = store._urlStateKey();
      if (!urlStateKey) return;

      const state: Record<string, unknown> = {
        filters: normalizeFilters(store.filters()),
      };
      if (store.sortField()) state['sortField'] = store.sortField();
      if (store.sortOrder() !== 1) state['sortOrder'] = store.sortOrder();
      if (store.first() !== 0) state['first'] = store.first();
      if (store.rows() !== 10) state['rows'] = store.rows();

      const queryParams = { ...route.snapshot.queryParams } as Record<string, unknown>;
      queryParams[urlStateKey] = JSON.stringify(state);

      void router.navigate([], {
        relativeTo: route,
        queryParams,
        replaceUrl: true,
      });
    }

    return {
      /** Returns debounced observable of load request params. Subscribe once in parent. */
      getLoadRequests$(): Observable<{
        filters: TableFilters;
        sortField: string | null;
        sortOrder: number;
        first: number;
        rows: number;
      }> {
        return _loadRequests$;
      },

      init(config: TableStateStoreInitConfig): void {
        patchState(store, {
          _urlStateKey: config.urlStateKey,
          _transformFiltersForBackend: config.transformFiltersForBackend ?? null,
        });

        const urlState = extractUrlState(config.urlStateKey);
        const mergedFilters: TableFilters = { ...config.defaultFilters, ...urlState.filters };

        if (config.navigationFilter) {
          mergedFilters[config.navigationFilter.field] = {
            value: config.navigationFilter.value,
            matchMode: 'equals',
          };
        }

        patchState(store, {
          defaultFilters: config.defaultFilters,
          filters: mergedFilters,
          sortField: urlState.sortField,
          sortOrder: urlState.sortOrder,
          first: urlState.first,
          rows: urlState.rows,
          initialized: true,
        });

        syncToUrl();
      },

      updateFilter(field: string, filterMetadata: { value: unknown; matchMode?: string }): void {
        const currentFilters = cloneDeep(store.filters());
        currentFilters[field] = {
          value: filterMetadata.value,
          matchMode: filterMetadata.matchMode ?? 'contains',
        };
        patchState(store, { filters: normalizeFilters(currentFilters), first: 0 });
        syncToUrl();
        _loadSubject.next();
      },

      applyPreset(presetFilters: TableFilters): void {
        patchState(store, { filters: normalizeFilters(presetFilters), first: 0 });
        syncToUrl();
        _loadSubject.next();
      },

      reset(): void {
        patchState(store, {
          filters: normalizeFilters(cloneDeep(store.defaultFilters())),
          sortField: null,
          sortOrder: 1,
          first: 0,
        });
        syncToUrl();
        _loadSubject.next();
      },

      updateSort(field: string, order: number): void {
        patchState(store, { sortField: field, sortOrder: order });
        syncToUrl();
        _loadSubject.next();
      },

      updatePagination(first: number, rows: number): void {
        patchState(store, { first, rows });
        syncToUrl();
        _loadSubject.next();
      },

      syncToUrl,
    };
  }),
);
